import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownFromLine, ArrowUpFromLine } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import "./RelatorioGoogleAds.css";
import DateRangePresets from "../../components/DateRangePresets";
import RelatorioSkeleton from "../../components/RelatorioSkeleton";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);


const API_GOOGLEADS = "/crm-wly/api/googleads";

const API_OPENAI = "/crm-wly/api/openai";

const AD_TYPE_LABELS = {
  RESPONSIVE_SEARCH_AD: "Anúncio de pesquisa responsivo",
  EXPANDED_TEXT_AD: "Anúncio de texto expandido",
  CALL_ONLY_AD: "Anúncio somente de chamada",
  IMAGE_AD: "Anúncio gráfico",
  VIDEO_AD: "Anúncio em vídeo",
};


async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
      "Content-Type": "application/json",
    },
  });

  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text().catch(() => "");

  if (!res.ok) {
    const msg =
      (data && data.error) ||
      (data && data.message) ||
      (typeof data === "string" ? data : "Erro na requisição");
    throw new Error(msg);
  }

  return data;
}


function fmtBRDate(iso) {
  if (!iso) return "";
  // aceita "YYYY-MM-DD" ou "YYYY-MM-DD HH:mm:ss"
  const s = String(iso).trim().slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function getAdTypeLabel(type) {
  return AD_TYPE_LABELS[type] || "Anúncio";
}

function fmtTaxaConversao(conversions, clicks) {
  const c = Number(conversions || 0);
  const k = Number(clicks || 0);
  if (!k || k <= 0) return "0,00%";
  const pct = (c / k) * 100;
  return `${pct.toFixed(2).replace(".", ",")}%`;
}

function primeiroDiaMesISO() {
  const d = new Date();
  d.setDate(1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function kpiHelpText(key) {
  const map = {
    impressions: "Quantidade de vezes que seus anúncios apareceram",
    clicks: "Total de cliques nos seus anúncios",
    ctr: "Taxa de cliques (cliques ÷ impressões)",
    cost: "Investimento total em tráfego pago no período",
    average_cpc: "Custo médio por clique",
    conversions: "Total de conversões registradas",
    cost_per_conversion: "Custo médio por conversão",
    conversions_value: "Valor total atribuído às conversões",
  };
  return map[key] || "";
}

function fmtMoneyBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtPctBR(n) {
  const v = Number(n || 0) * 100;
  return `${v.toFixed(2).replace(".", ",")}%`;
}

function renderStatusIcon(status) {
  const s = String(status || "").toUpperCase();

  if (s === "ENABLED") return <span className="gads-status gads-status-enabled">🟢</span>;
  if (s === "PAUSED") return <span className="gads-status gads-status-paused">⏸️</span>;
  if (s === "REMOVED") return <span className="gads-status gads-status-removed">❌</span>;

  return null;
}

// isAdmin do seu app (localStorage)
function readIsAdminFromLocalStorage() {
  const raw = localStorage.getItem("isAdmin");
  if (raw !== null && raw !== undefined) {
    const v = String(raw).trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "no") return false;
  }
  try {
    const userRaw = localStorage.getItem("usuario");
    if (userRaw) {
      const u = JSON.parse(userRaw);
      if (u?.isAdmin === true || u?.isAdmin === 1) return true;
      if (String(u?.tipo_usuario || "").toLowerCase() === "master") return true;
      if (String(u?.tipo || "").toLowerCase() === "master") return true;
    }
  } catch (_) {}
  return false;
}

// pega usuário logado (pra mostrar no filtro quando não é master)
async function fetchMeuUser() {
  // seu endpoint é /crm-wly/getUser.php (retorna o logado)
  const r = await fetchJSON("/crm-wly/getUser.php", { method: "GET" });
  // normalmente: { success:true, data:{...} }
  if (r?.data) return r.data;
  if (r?.success && r?.data) return r.data;
  return r;
}

// lista usuários (master)
async function fetchUsuariosListagem() {
  // você confirmou que existe esse endpoint
  const r = await fetchJSON("/crm-wly/listagem_usuarios.php", { method: "GET" });
  // retorna { tipo_usuario, usuarios:[...] }
  return Array.isArray(r?.usuarios) ? r.usuarios : [];
}

// helpers
function hojeISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function diasAtrasISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fmtBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtInt(n) {
  return Number(n || 0).toLocaleString("pt-BR");
}
function fmtPct(n) {
  const v = Number(n || 0) * 100;
  return `${v.toFixed(2).replace(".", ",")}%`;
}
function fmtNum(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}
function qs(params = {}) {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    u.set(k, String(v));
  });
  const s = u.toString();
  return s ? `?${s}` : "";
}


export default function RelatorioGoogleAds() {
  const isAdmin = useMemo(() => readIsAdminFromLocalStorage(), []);

  // filtros
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState(""); // string (select)
  const [usuarioLabel, setUsuarioLabel] = useState(""); // nome p/ topo

  const [dateStart, setDateStart] = useState(primeiroDiaMesISO());
  const [dateEnd, setDateEnd] = useState(hojeISO());

  const [applied, setApplied] = useState(false);


  // token status
  const [tokenInfo, setTokenInfo] = useState({
    connected: false,
    access_token_valid: false,
    access_token_expires_at: null,
    loading: true,
    error: "",
  });

  // dados resumo
  const [resumo, setResumo] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [errorResumo, setErrorResumo] = useState("");

     // ===== CAMPANHAS =====
    const [campanhas, setCampanhas] = useState([]);
    const [loadingCampanhas, setLoadingCampanhas] = useState(false);
    const [errorCampanhas, setErrorCampanhas] = useState("");

    const [campanhasQuery, setCampanhasQuery] = useState("");
    const [campanhasSort, setCampanhasSort] = useState("cost_desc"); // default
    const [campanhaSelecionadaId, setCampanhaSelecionadaId] = useState(""); // para usar depois em Termos/Anúncios etc
  const [campSearch, setCampSearch] = useState("");
  const [campSort, setCampSort] = useState("cost_desc"); // default: maior custo

  // --- TERMOS DE PESQUISA ---
const [termos, setTermos] = useState([]);
const [loadingTermos, setLoadingTermos] = useState(false);
const [errorTermos, setErrorTermos] = useState("");

const [sortKey, setSortKey] = useState("impressions"); // default
const [sortDir, setSortDir] = useState("desc"); // desc = maior primeiro

const [page, setPage] = useState(1);
const [pageSize, setPageSize] = useState(25);

// --- GRUPOS DE ANÚNCIOS ---
const [grupos, setGrupos] = useState([]);
const [loadingGrupos, setLoadingGrupos] = useState(false);
const [errorGrupos, setErrorGrupos] = useState("");

// ordenação (front)
const [gruposSortKey, setGruposSortKey] = useState("cost"); // default
const [gruposSortDir, setGruposSortDir] = useState("desc");

// paginação (front)
const [gruposPage, setGruposPage] = useState(1);
const [gruposPageSize, setGruposPageSize] = useState(50);


// --- ANÚNCIOS ---
const [anuncios, setAnuncios] = useState([]);
const [loadingAnuncios, setLoadingAnuncios] = useState(false);
const [errorAnuncios, setErrorAnuncios] = useState("");

const [anunciosSortKey, setAnunciosSortKey] = useState("cost");
const [anunciosSortDir, setAnunciosSortDir] = useState("desc");

const [anunciosPage, setAnunciosPage] = useState(1);
const [anunciosPageSize, setAnunciosPageSize] = useState(25);


// =========================
// LOCALIZAÇÕES (User location)
// =========================
const [locs, setLocs] = useState([]);
const [loadingLocs, setLoadingLocs] = useState(false);
const [locsError, setLocsError] = useState("");

const [locsPage, setLocsPage] = useState(1);
const [locsPageSize, setLocsPageSize] = useState(25);
const [locsSortKey, setLocsSortKey] = useState("cost"); // cost | conversions | clicks | impressions | ctr | cpc | cpa
const [locsSortDir, setLocsSortDir] = useState("desc"); // asc | desc

const [hasLoaded, setHasLoaded] = useState(false);
const [loadingAll, setLoadingAll] = useState(false);
const [errorAll, setErrorAll] = useState("");

const [dailyRows, setDailyRows] = useState([]);
const [dailyErr, setDailyErr] = useState("");


const [aiOpen, setAiOpen] = useState(true);
const [aiLoading, setAiLoading] = useState(false);
const [aiError, setAiError] = useState("");
const [aiData, setAiData] = useState(null); 

const [aiExpanded, setAiExpanded] = useState(true);


  const bootedRef = useRef(false);


  const campanhasFiltradas = useMemo(() => {
  const q = normalizeText(campanhasQuery);

  const mapped = (campanhas || []).map((c) => {
    // normaliza campos comuns (independente do nome exato vindo do PHP)
    const id = String(pick(c, ["campaign_id", "id", "campaignId", "resource_name"], ""));
    const nome = String(pick(c, ["campaign_name", "name", "campaign", "nome"], ""));

    const impressions = toNumberSafe(pick(c, ["impressions", "impr", "impressoes"], 0));
    const clicks = toNumberSafe(pick(c, ["clicks", "cliques"], 0));
    const ctr = toNumberSafe(pick(c, ["ctr"], 0));
    const cost = toNumberSafe(pick(c, ["cost", "cost_micros_brl", "custo"], 0));
    const average_cpc = toNumberSafe(pick(c, ["average_cpc", "avg_cpc", "cpc_medio"], 0));
    const conversions = toNumberSafe(pick(c, ["conversions", "conv"], 0));
    const cost_per_conversion = toNumberSafe(pick(c, ["cost_per_conversion", "custo_por_conversao"], 0));
    const conversions_value = toNumberSafe(pick(c, ["conversions_value", "conversion_value", "valor_conversao"], 0));

    const status = String(pick(c, ["status", "campaign_status"], ""));

    return {
      __raw: c,
      id,
      nome,
      status,
      impressions,
      clicks,
      ctr,
      cost,
      average_cpc,
      conversions,
      cost_per_conversion,
      conversions_value,
    };
  });

  const filtered = !q
    ? mapped
    : mapped.filter((x) => {
        const hay = normalizeText(`${x.nome} ${x.status} ${x.id}`);
        return hay.includes(q);
      });

  const sorters = {
    cost_desc: (a, b) => b.cost - a.cost,
    cost_asc: (a, b) => a.cost - b.cost,
    clicks_desc: (a, b) => b.clicks - a.clicks,
    clicks_asc: (a, b) => a.clicks - b.clicks,
    impr_desc: (a, b) => b.impressions - a.impressions,
    impr_asc: (a, b) => a.impressions - b.impressions,
    conv_desc: (a, b) => b.conversions - a.conversions,
    conv_asc: (a, b) => a.conversions - b.conversions,
    ctr_desc: (a, b) => b.ctr - a.ctr,
    ctr_asc: (a, b) => a.ctr - b.ctr,
    name_asc: (a, b) => a.nome.localeCompare(b.nome),
    name_desc: (a, b) => b.nome.localeCompare(a.nome),
  };

  const sorter = sorters[campanhasSort] || sorters.cost_desc;
  return filtered.slice().sort(sorter);
}, [campanhas, campanhasQuery, campanhasSort]);


function headerSortLabel(key, label) {
  const active = sortKey === key;
  const arrow = !active ? "" : (sortDir === "asc" ? " ▲" : " ▼");

  return (
    <button
      type="button"
      className={`gads-terms-thbtn ${active ? "is-active" : ""}`}
      onClick={() => {
        toggleSort(key);
        setPage(1);
      }}
      title="Ordenar"
    >
      {label}{arrow}
    </button>
  );
}
async function carregarAnaliseIA({ force = false } = {}) {
  try {
    setAiError("");
    setAiLoading(true);

    if (!usuarioId) throw new Error("Selecione um usuário.");

    const params = new URLSearchParams();
    params.set("date_start", dateStart);
    params.set("date_end", dateEnd);

    // master consulta outro usuário
    if (isAdmin && usuarioId) {
      params.set("usuario_id", String(usuarioId));
    }

    // se o backend suportar, isso ajuda a ignorar cache do servidor
    if (force) params.set("force", "1");

    // cache-buster do browser (sempre bom)
    params.set("_t", String(Date.now()));

    const url = `${API_OPENAI}/analisar_googleads.php?${params.toString()}`;

    const j = await fetchJSON(url, { method: "GET" });

    if (!j?.success) {
      throw new Error(j?.error || "Falha ao gerar análise de IA.");
    }

    setAiData(j);
    setAiOpen(true); // abre a sessão quando chegar resultado
  } catch (e) {
    setAiData(null);
    setAiError(e?.message || "Erro ao gerar análise de IA.");
  } finally {
    setAiLoading(false);
  }
}


async function carregarSerieDiaria(uid, override = {}) {
  const { ds, de } = getPeriodo(override);

  try {
    setDailyErr("");
    setDailyRows([]);

    const params = new URLSearchParams({
      usuario_id: String(uid),
      date_start: ds,
      date_end: de,
      daily: "1",
    });

    const r = await fetch(`${API_GOOGLEADS}/relatorio_resumo.php?${params.toString()}`, {
      credentials: "include",
    });

    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.success) throw new Error(j?.error || `Erro HTTP ${r.status}`);

    setDailyRows(Array.isArray(j.data?.rows) ? j.data.rows : []);
  } catch (e) {
    setDailyRows([]);
    setDailyErr(e?.message || "Erro ao carregar evolução");
  }
}


async function carregarAnuncios() {
  try {
    setErrorAnuncios("");
    setLoadingAnuncios(true);
    setAnuncios([]);

    if (!usuarioId) throw new Error("Selecione um usuário.");

    const tk = await ensureAccessToken(usuarioId);
    if (!tk?.connected) throw new Error("Este usuário não tem integração ativa do Google Ads.");

    const url = `${API_GOOGLEADS}/relatorio_anuncios.php${qs({
      ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
      date_start: dateStart,
      date_end: dateEnd,
      ...(campanhaSelecionadaId ? { campaign_id: Number(campanhaSelecionadaId) } : {}),
    })}`;

    const r = await fetchJSON(url, { method: "GET" });
    if (!r?.success) throw new Error(r?.error || "Falha ao carregar anúncios");

    const rows = r?.data?.rows || [];
    setAnuncios(Array.isArray(rows) ? rows : []);
  } catch (e) {
    setErrorAnuncios(e?.message || "Erro ao carregar anúncios");
  } finally {
    setLoadingAnuncios(false);
  }
}

useEffect(() => {
  if (!applied) return;

  if (!usuarioId) return;
  carregarAnuncios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [usuarioId, dateStart, dateEnd, campanhaSelecionadaId]);

useEffect(() => {
  setAnunciosPage(1);
}, [anunciosSortKey, anunciosSortDir, anunciosPageSize]);


 async function carregarCampanhas() {
    try {
      setErrorCampanhas("");
      setLoadingCampanhas(true);
      setCampanhas([]);

      if (!usuarioId) throw new Error("Selecione um usuário.");

      // garante token (mesmo fluxo da visão geral)
      const tk = await ensureAccessToken(usuarioId);
      setTokenInfo((p) => ({ ...p, ...tk, loading: false, error: "" }));
      if (!tk.connected) {
        throw new Error("Nenhuma integração Google Ads ativa encontrada para este usuário.");
      }

      const url = `${API_GOOGLEADS}/relatorio_campanhas.php${qs({
        ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
        date_start: dateStart,
        date_end: dateEnd,
      })}`;

      const r = await fetchJSON(url, { method: "GET" });
      if (!r?.success) throw new Error(r?.error || "Falha ao carregar campanhas");

      const list = Array.isArray(r?.data?.campanhas) ? r.data.campanhas : [];
      setCampanhas(list);
    } catch (e) {
      setErrorCampanhas(e?.message || "Erro ao carregar campanhas");
    } finally {
      setLoadingCampanhas(false);
    }
  }

  // =========================
// GRÁFICO (COLUNAS): Campanha 1 x Campanha 2 por métricas (INDEXADO 0–100)
// =========================
  const campanhasView = useMemo(() => {
    const q = String(campSearch || "").trim().toLowerCase();

    let list = Array.isArray(campanhas) ? [...campanhas] : [];
    if (q) {
      list = list.filter((c) =>
        String(c?.campaign_name || "").toLowerCase().includes(q)
      );
    }

    const sorters = {
      cost_desc: (a, b) => (Number(b.cost || 0) - Number(a.cost || 0)),
      clicks_desc: (a, b) => (Number(b.clicks || 0) - Number(a.clicks || 0)),
      conv_desc: (a, b) => (Number(b.conversions || 0) - Number(a.conversions || 0)),
      ctr_desc: (a, b) => (Number(b.ctr || 0) - Number(a.ctr || 0)),
      impr_desc: (a, b) => (Number(b.impressions || 0) - Number(a.impressions || 0)),
    };

    list.sort(sorters[campSort] || sorters.cost_desc);
    return list;
  }, [campanhas, campSearch, campSort]);

const top2CampanhasPorConv = useMemo(() => {
  const list = Array.isArray(campanhasView) ? [...campanhasView] : [];
  list.sort((a, b) => Number(b?.conversions || 0) - Number(a?.conversions || 0));
  return list.slice(0, 2);
}, [campanhasView]);

const cmpCampanhas2Bar = useMemo(() => {
  if (!top2CampanhasPorConv || top2CampanhasPorConv.length === 0) return null;

  const c1 = top2CampanhasPorConv[0] || null;
  const c2 = top2CampanhasPorConv[1] || null;
  if (!c1) return null;

  const getName = (c) => String(pick(c, ["campaign_name", "name", "campaign"], "Campanha"));
  const num = (v) => toNumberSafe(v);

  const impressions = (c) => num(pick(c, ["impressions"], 0));
  const clicks = (c) => num(pick(c, ["clicks"], 0));
  const conversions = (c) => num(pick(c, ["conversions"], 0));

  // custo em R$ (usa cost se existir; senão converte cost_micros)
  const costBRL = (c) => {
    const direct = pick(c, ["cost"], null);
    if (direct !== null && direct !== undefined) return num(direct);
    const micros = num(pick(c, ["cost_micros", "costMicros"], 0));
    return micros / 1_000_000;
  };

  const ctr = (c) => {
    const direct = pick(c, ["ctr"], null);
    if (direct !== null && direct !== undefined) return num(direct); // 0-1
    const imp = impressions(c);
    const clk = clicks(c);
    return imp > 0 ? (clk / imp) : 0;
  };

  const avgCpc = (c) => {
    const direct = pick(c, ["average_cpc", "avg_cpc"], null);
    if (direct !== null && direct !== undefined) return num(direct);
    const clk = clicks(c);
    const cost = costBRL(c);
    return clk > 0 ? (cost / clk) : 0;
  };

  const convRate = (c) => {
    const direct = pick(c, ["conv_rate", "conversion_rate"], null);
    if (direct !== null && direct !== undefined) return num(direct); // 0-1
    const clk = clicks(c);
    const conv = conversions(c);
    return clk > 0 ? (conv / clk) : 0;
  };

  const costPerConv = (c) => {
    const direct = pick(c, ["cost_per_conversion", "cpa"], null);
    if (direct !== null && direct !== undefined) return num(direct);
    const conv = conversions(c);
    const cost = costBRL(c);
    return conv > 0 ? (cost / conv) : 0;
  };

  // ✅ métricas (como seu desenho)
  const labels = [
    "CTR",
    "Impressões",
    "Custo",
    "Conversões",
    "Cliques",
    "CPC médio",
    "Taxa conv.",
    "Custo/Conv",
  ];

  // valores reais
  const raw1 = [
    ctr(c1),
    impressions(c1),
    costBRL(c1),
    conversions(c1),
    clicks(c1),
    avgCpc(c1),
    convRate(c1),
    costPerConv(c1),
  ];

  const raw2 = c2
    ? [
        ctr(c2),
        impressions(c2),
        costBRL(c2),
        conversions(c2),
        clicks(c2),
        avgCpc(c2),
        convRate(c2),
        costPerConv(c2),
      ]
    : labels.map(() => 0);

  // ✅ INDEXAÇÃO 0–100 por métrica (maior vira 100)
  const idx1 = raw1.map((v, i) => {
    const m = Math.max(Number(v || 0), Number(raw2[i] || 0));
    return m > 0 ? (Number(v || 0) / m) * 100 : 0;
  });

  const idx2 = raw2.map((v, i) => {
    const m = Math.max(Number(raw1[i] || 0), Number(v || 0));
    return m > 0 ? (Number(v || 0) / m) * 100 : 0;
  });

  return {
    labels,
    datasets: [
      {
        label: getName(c1),
        data: idx1,
        // guarda valores reais pra tooltip
        _raw: raw1,
      },
      {
        label: c2 ? getName(c2) : "—",
        data: idx2,
        _raw: raw2,
      },
    ],
  };
}, [top2CampanhasPorConv]);

  function exportarCampanhasCSV() {
    const rows = campanhasView;
    if (!rows.length) return;

    const header = [
      "Campanha",
      "Status",
      "Impressões",
      "Cliques",
      "CTR",
      "Custo",
      "CPC Médio",
      "Conversões",
      "Custo/Conv",
      "Valor Conv",
    ];

    const csv = [
      header.join(";"),
      ...rows.map((c) => [
        `"${String(c?.campaign_name || "").replaceAll('"', '""')}"`,
        `"${String(c?.status || "")}"`,
        String(c?.impressions ?? 0),
        String(c?.clicks ?? 0),
        String(((Number(c?.ctr || 0) * 100).toFixed(2)).replace(".", ",")) + "%",
        String((Number(c?.cost || 0)).toFixed(2)).replace(".", ","),
        String((Number(c?.average_cpc || 0)).toFixed(2)).replace(".", ","),
        String((Number(c?.conversions || 0)).toFixed(2)).replace(".", ","),
        String((Number(c?.cost_per_conversion || 0)).toFixed(2)).replace(".", ","),
        String((Number(c?.conversions_value || 0)).toFixed(2)).replace(".", ","),
      ].join(";"))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `campanhas_googleads_${dateStart}_a_${dateEnd}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }


  // 1) carregar usuários + setar default do filtro igual Meta
  useEffect(() => {
    (async () => {
      try {
        if (bootedRef.current) return;
        bootedRef.current = true;

        if (isAdmin) {
          const list = await fetchUsuariosListagem();
          setUsuarios(list);

          // default: vazio até o cara escolher (igual meta costuma fazer),
          // mas se quiser auto-selecionar o primeiro, descomenta abaixo:
          // if (list.length > 0) setUsuarioId(String(list[0].id));
        } else {
          const me = await fetchMeuUser();
          // força o filtro pro usuário logado
          setUsuarios([{ id: me?.id, nome: me?.nome, email: me?.email }]);
          setUsuarioId(String(me?.id || ""));
        }
      } catch (e) {
        // se der ruim aqui, ainda dá pra usar a página (mas sem select)
        console.error(e);
      }
    })();
  }, [isAdmin]);

  // label do usuário selecionado
  useEffect(() => {
    if (!usuarioId) {
      setUsuarioLabel("");
      return;
    }
    const u = usuarios.find((x) => String(x?.id) === String(usuarioId));
    setUsuarioLabel(u?.nome || u?.nome_empresa || u?.email || `Usuário ${usuarioId}`);
  }, [usuarios, usuarioId]);

  // 2) fluxo automático de token: status -> refresh se precisar
  async function ensureAccessToken(uId) {
    const uid = uId ? Number(uId) : null;

    // token_status GET
    const statusUrl = `${API_GOOGLEADS}/token_status.php${qs(
      isAdmin ? { usuario_id: uid } : {}
    )}`;

    const st = await fetchJSON(statusUrl, { method: "GET" });

    // esperado: { success:true, connected:true/false, access_token_valid, access_token_expires_at }
    const connected = !!st?.connected;
    const valid = !!st?.access_token_valid;

    if (!connected) {
      return {
        connected: false,
        access_token_valid: false,
        access_token_expires_at: st?.access_token_expires_at || null,
      };
    }

    if (valid) {
      return {
        connected: true,
        access_token_valid: true,
        access_token_expires_at: st?.access_token_expires_at || null,
      };
    }

    // refresh GET (você disse que mudou pra GET)
    const refreshUrl = `${API_GOOGLEADS}/token_refresh.php${qs(
      isAdmin ? { usuario_id: uid } : {}
    )}`;
    const rf = await fetchJSON(refreshUrl, { method: "GET" });

    return {
      connected: true,
      access_token_valid: true,
      access_token_expires_at: rf?.access_token_expires_at || null,
    };
  }

  async function carregarVisaoGeral(override = {}) {
  // ✅ garante que a chamada use o período “aplicado” (ou override)
  const ds = override.date_start ?? dateStart;
  const de = override.date_end ?? dateEnd;

  try {
    setErrorResumo("");
    setLoadingResumo(true);
    setResumo(null);

    if (!usuarioId) throw new Error("Selecione um usuário.");

    // 2.1) token automático
    setTokenInfo((p) => ({ ...p, loading: true, error: "" }));

    let tk;
    try {
      tk = await ensureAccessToken(usuarioId);

      setTokenInfo((p) => ({
        ...p,
        ...tk,
        loading: false,
        error: "",
      }));

      if (!tk?.connected) {
        throw new Error(
          "Este usuário não tem integração ativa do Google Ads (Contate um Administrador)."
        );
      }
    } catch (e) {
      setTokenInfo((p) => ({
        ...p,
        loading: false,
        error: e?.message || "Falha ao validar/renovar token",
      }));
      throw e;
    }

    // 2.2) chama relatorio_resumo (GET)
    const resumoUrl = `${API_GOOGLEADS}/relatorio_resumo.php${qs({
      ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
      date_start: ds,
      date_end: de,
    })}`;

    const r = await fetchJSON(resumoUrl, { method: "GET" });
    if (!r?.success) throw new Error(r?.error || "Falha ao carregar resumo");

    setResumo(r?.data || null);
  } catch (e) {
    setErrorResumo(e?.message || "Erro ao carregar visão geral");
  } finally {
    setLoadingResumo(false);
  }
}


  // auto-load: quando tiver usuarioId (igual meta já carrega assim que escolhe)
  useEffect(() => {
     if (!applied) return;
    if (!usuarioId) return;
    // carrega sozinho ao trocar usuário (pra ficar “meta-like”)
    carregarVisaoGeral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId]);

async function carregarLocalizacoes(override = {}) {
  const ds = override.date_start ?? dateStart;
  const de = override.date_end ?? dateEnd;

  if (!ds || !de) return;

  setLoadingLocs(true);
  setLocsError("");

  try {
    const params = new URLSearchParams();
    params.set("date_start", ds);
    params.set("date_end", de);

    // mesma regra do resto do relatório: master pode consultar outro usuário
    if (isAdmin && usuarioId) {
      params.set("usuario_id", String(usuarioId));
    }

    const j = await fetchJSON(
      `${API_GOOGLEADS}/relatorio_localizacoes.php?${params.toString()}`,
      { method: "GET" }
    );

    const rows = j?.data?.rows || [];
    setLocs(Array.isArray(rows) ? rows : []);
    setLocsPage(1);
  } catch (e) {
    setLocs([]);
    setLocsError(e?.message || "Erro ao carregar localizações");
  } finally {
    setLoadingLocs(false);
  }
}

function getPeriodo(override = {}) {
  return {
    ds: override.date_start ?? dateStart,
    de: override.date_end ?? dateEnd,
  };
}

async function carregarGruposAnuncios() {
  try {
    setErrorGrupos("");
    setLoadingGrupos(true);
    setGrupos([]);

    if (!usuarioId) throw new Error("Selecione um usuário.");

    // garante token (mesmo padrão das campanhas)
    const tk = await ensureAccessToken(usuarioId);
    if (!tk?.connected) throw new Error("Este usuário não tem integração ativa do Google Ads.");

    const url = `${API_GOOGLEADS}/relatorio_grupos.php${qs({
      ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
      date_start: dateStart,
      date_end: dateEnd,

      // se quiser filtrar por campanha selecionada (se existir)
      ...(campanhaSelecionadaId ? { campaign_id: Number(campanhaSelecionadaId) } : {}),
    })}`;

    const r = await fetchJSON(url, { method: "GET" });
    if (!r?.success) throw new Error(r?.error || "Falha ao carregar grupos de anúncios");

    const rows = r?.data?.rows || [];
    setGrupos(Array.isArray(rows) ? rows : []);
  } catch (e) {
    setErrorGrupos(e?.message || "Erro ao carregar grupos de anúncios");
  } finally {
    setLoadingGrupos(false);
  }
}

// auto-load quando trocar usuario/período/campanha
useEffect(() => {
     if (!applied) return;
  if (!usuarioId) return;
  carregarGruposAnuncios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [usuarioId, dateStart, dateEnd, campanhaSelecionadaId]);

// sempre que mudar sort/pagina-size, volta pra página 1
useEffect(() => {
  setGruposPage(1);
}, [gruposSortKey, gruposSortDir, gruposPageSize]);


async function carregarTermosPesquisa(override = {}) {
  const { ds, de } = getPeriodo(override);

  try {
    setErrorTermos("");
    setLoadingTermos(true);
    setTermos([]);

    if (!usuarioId) throw new Error("Selecione um usuário.");

    const url = `${API_GOOGLEADS}/relatorio_termos_pesquisa.php${qs({
      ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
      date_start: ds,
      date_end: de,
    })}`;

    const r = await fetchJSON(url, { method: "GET" });
    if (!r?.success) throw new Error(r?.error || "Falha ao carregar termos de pesquisa");

    const rows = r?.data?.termos || r?.data?.rows || [];
    setTermos(Array.isArray(rows) ? rows : []);
  } catch (e) {
    setErrorTermos(e?.message || "Erro ao carregar termos de pesquisa");
  } finally {
    setLoadingTermos(false);
  }
}


useEffect(() => {
     if (!applied) return;
  if (!usuarioId) return;
  carregarTermosPesquisa();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [usuarioId, dateStart, dateEnd]);

function toggleSortAnuncios(key) {
  if (anunciosSortKey === key) {
    setAnunciosSortDir((d) => (d === "asc" ? "desc" : "asc"));
  } else {
    setAnunciosSortKey(key);
    setAnunciosSortDir("desc");
  }
}

// pega "text" de assets do RSA
function pickAssetTextList(arr, limit = 3) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const it of arr) {
    const t = String(it?.text || "").trim();
    if (t) out.push(t);
    if (out.length >= limit) break;
  }
  return out;
}

function buildAdPreview(row) {
  const type = String(row?.ad_type || "").toUpperCase();
  const finalUrl = Array.isArray(row?.final_urls) ? row.final_urls[0] : "";
  const urlObj = finalUrl ? new URL(finalUrl, window.location.origin) : null;
  const host = urlObj ? urlObj.host.replace(/^www\./, "") : "";

  // RSA
  const rsaH = pickAssetTextList(row?.rsa?.headlines, 3);
  const rsaD = pickAssetTextList(row?.rsa?.descriptions, 2);
  const path1 = row?.rsa?.path1 ? String(row.rsa.path1) : "";
  const path2 = row?.rsa?.path2 ? String(row.rsa.path2) : "";

  // ETA
  const eta = row?.eta || {};
  const etaH = [eta?.h1, eta?.h2, eta?.h3].filter(Boolean).map(String);
  const etaD = [eta?.d1, eta?.d2].filter(Boolean).map(String);

  const headline =
    rsaH.length ? rsaH.join(" | ") :
    etaH.length ? etaH.filter(Boolean).slice(0, 3).join(" | ") :
    (row?.ad_name ? String(row.ad_name) : "—");

  const desc =
    rsaD.length ? rsaD.join(" ") :
    etaD.length ? etaD.join(" ") :
    "";

  const displayUrl = host
    ? `${host}${path1 ? `/${path1}` : ""}${path2 ? `/${path2}` : ""}`
    : (finalUrl || "");

  return { type, headline, desc, displayUrl, finalUrl };
}


async function aplicarPeriodo(override = {}) {
  if (!usuarioId) {
    setErrorAll("Selecione um usuário para carregar os relatórios.");
    return;
  }

  const ds = override.date_start ?? dateStart;
  const de = override.date_end ?? dateEnd;

  setErrorAll("");
  setLoadingAll(true);
  setHasLoaded(false);

  try {
    await Promise.all([
      carregarVisaoGeral({ date_start: ds, date_end: de }),
      typeof carregarCampanhas === "function"
        ? carregarCampanhas({ date_start: ds, date_end: de })
        : Promise.resolve(),
      carregarSerieDiaria(usuarioId, { date_start: ds, date_end: de }),
      carregarTermosPesquisa({ date_start: ds, date_end: de }),
      carregarGruposAnuncios({ date_start: ds, date_end: de }),
      carregarAnuncios({ date_start: ds, date_end: de }),
      carregarLocalizacoes({ date_start: ds, date_end: de }),
    ]);

    setHasLoaded(true);
    setApplied(true);
  } catch (e) {
    setErrorAll(e?.message || "Não foi possível carregar os dados.");
    setHasLoaded(false);
  } finally {
    setLoadingAll(false);
  }
}



  function pick(obj, keys, fallback = null) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
}

function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}


function resetarRelatorioVisual() {
  // “trava” a renderização do miolo
  setHasLoaded(false);

  // limpa blocos de dados
  setResumo(null);
  setCampanhas([]);
  setTermos([]);
  setGrupos([]);
  setAnuncios([]);
  setLocs([]);
  setDailyRows([]);

  // limpa seleção dependente
  setCampanhaSelecionadaId("");

  // limpa IA
  setAiData(null);
  setAiError("");
  setAiLoading(false);
  setAiOpen(true);
  setAiExpanded(true);

  // limpa erros
  setErrorAll("");
  setErrorResumo("");
  setErrorCampanhas("");
  setErrorTermos("");
  setErrorGrupos("");
  setErrorAnuncios("");
  setLocsError("");
  setDailyErr("");

  // garante que não fica “carregando” preso
  setLoadingAll(false);

  // (se você continuar usando applied)
  setApplied(false);
}


function toNumberSafe(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toggleSortGrupos(key) {
  if (gruposSortKey === key) {
    setGruposSortDir((d) => (d === "asc" ? "desc" : "asc"));
  } else {
    setGruposSortKey(key);
    setGruposSortDir("desc");
  }
}


function toggleSort(key) {
  if (sortKey === key) {
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  } else {
    setSortKey(key);
    setSortDir("desc");
  }
}

function sortValue(row, key) {
  // números
  const numKeys = new Set([
    "impressions",
    "clicks",
    "cost",
    "ctr",
    "average_cpc",
    "conversions",
    "cost_per_conversion",
    "conversions_value",
  ]);

  if (numKeys.has(key)) return Number(row?.[key] ?? 0);

  // textos
  return String(row?.[key] ?? "").toLowerCase();
}


function downloadCSV(filename, rows, headers) {
  // headers: [{ key, label, format? }]
  const escapeCSV = (val) => {
    const s = String(val ?? "");
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [];
  lines.push(headers.map((h) => escapeCSV(h.label)).join(";"));

  for (const r of rows) {
    const line = headers
      .map((h) => {
        const raw = r?.[h.key];
        const out = typeof h.format === "function" ? h.format(raw, r) : raw;
        return escapeCSV(out);
      })
      .join(";");
    lines.push(line);
  }

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}



async function carregarCampanhas() {
  try {
    setErrorCampanhas("");
    setLoadingCampanhas(true);
    setCampanhas([]);

    if (!usuarioId) throw new Error("Selecione um usuário.");
    // garante token antes (usa sua função existente)
    const tk = await ensureAccessToken(usuarioId);
    if (!tk?.connected) throw new Error("Este usuário não tem integração ativa do Google Ads.");

    const url = `${API_GOOGLEADS}/relatorio_campanhas.php${qs({
      ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
      date_start: dateStart,
      date_end: dateEnd,
    })}`;

    const r = await fetchJSON(url, { method: "GET" });
    if (!r?.success) throw new Error(r?.error || "Falha ao carregar campanhas");

    // aceita tanto data direto quanto data.campanhas
    const data = r?.data ?? r;
    const list =
      Array.isArray(data?.campanhas) ? data.campanhas :
      Array.isArray(data?.rows) ? data.rows :
      Array.isArray(data) ? data :
      [];

    setCampanhas(list);
  } catch (e) {
    setErrorCampanhas(e?.message || "Erro ao carregar campanhas");
  } finally {
    setLoadingCampanhas(false);
  }
}

// carrega campanhas quando trocar usuario ou período (depois você pode trocar pra “só ao clicar Aplicar” se preferir)
useEffect(() => {
     if (!applied) return;
  if (!usuarioId) return;
  carregarCampanhas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [usuarioId, dateStart, dateEnd]);

function resetPeriodo() {
  setDateStart(diasAtrasISO(30));
  setDateEnd(hojeISO());
  setTimeout(() => {
    carregarVisaoGeral();
    carregarCampanhas?.();
    carregarSerieDiaria(usuarioId);
    carregarTermosPesquisa();   
    carregarGruposAnuncios();
  }, 0);
}



  const totais = resumo?.totais || null;

  const PASTEL = [
  "#EAF2FF", // azul bem claro
  "#EFFFF4", // verde bem claro
  "#FFF4EA", // laranja bem claro
  "#F6EEFF", // roxo bem claro
  "#FFEFF3", // rosa bem claro
  "#F3F5F7", // cinza claro
];

function pickBg(i) {
  return PASTEL[i % PASTEL.length];
}

function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dateFromYmd(ymd) {
  // evita “voltar um dia” por timezone
  return new Date(`${ymd}T12:00:00`);
}


function fmtValue(v) {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") {
    // não inventa unidade: só deixa com até 2 casas
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
}

function renderEvidence(evidence) {
  if (!Array.isArray(evidence) || evidence.length === 0) return null;

  return (
    <div className="gads-ai-ev">
      {evidence.map((ev, idx) => (
        <div key={idx} className="gads-ai-chip">
          <span className="gads-ai-chip-dim">{ev?.dimension || "info"}</span>
          <span className="gads-ai-chip-name">{ev?.name || "-"}</span>
          <span className="gads-ai-chip-metric">{ev?.metric || "-"}</span>
          <span className="gads-ai-chip-value">{fmtValue(ev?.value)}</span>
        </div>
      ))}
    </div>
  );
}

function renderRecCards(list) {
  if (!Array.isArray(list) || list.length === 0) return null;

  return (
    <div className="gads-ai-grid">
      {list.map((rec, i) => (
        <div
          key={i}
          className="gads-ai-card"
          style={{ background: pickBg(i) }}
        >
          <div className="gads-ai-card-title">{rec?.title || "Recomendação"}</div>

          {!!rec?.why && (
            <div className="gads-ai-line">
              <span className="gads-ai-label">Por quê:</span>
              <span className="gads-ai-text">{rec.why}</span>
            </div>
          )}

          {renderEvidence(rec?.evidence)}

          {Array.isArray(rec?.how_to) && rec.how_to.length > 0 && (
            <div className="gads-ai-line" style={{ marginTop: 8 }}>
              <span className="gads-ai-label">Como aplicar:</span>
              <ol className="gads-ai-ol">
                {rec.how_to.map((s, idx) => (
                  <li key={idx}>{s}</li>
                ))}
              </ol>
            </div>
          )}

          <div className="gads-ai-meta">
            {!!rec?.expected_impact && (
              <span className="gads-ai-pill">Impacto: {rec.expected_impact}</span>
            )}
            {!!rec?.effort && (
              <span className="gads-ai-pill">Esforço: {rec.effort}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


  function fmtPctBRFromRatio(ratio) {
  const v = Number(ratio || 0) * 100;
  return `${v.toFixed(2).replace(".", ",")}%`;
}

// dentro do componente, com totais disponível:
const convRate = (Number(totais?.clicks || 0) > 0)
  ? (Number(totais?.conversions || 0) / Number(totais?.clicks || 0))
  : 0;


  return (
  <div className="gads-rel-page">
    {/* TÍTULO FORA DO CONTAINER (igual Meta) */}
    <h1 className="relatorio-meta-titulo">Relatório Google Ads</h1>


    <div className="gads-rel-card">
      {/* TOPO (agora sem repetir título grande dentro do card) */}

      <div className="gads-logo-wrap">
  <img
    className="gads-logo"
    src={`https://welaunchyou.com.br/crm-wly/uploads/logos/google-ads-logo.png`}
    alt="Google Ads"
  />
</div>

      <div className="gads-rel-top">
        <div className="gads-rel-titlewrap">
          <div className="gads-rel-sub">
            {usuarioLabel ? (
              <>
                Usuário: <b>{usuarioLabel}</b>
              </>
            ) : (
              "Selecione um usuário para ver o relatório."
            )}
          </div>
        </div>

        <div className="gads-rel-token">
          <div className="gads-rel-token-line">
            <span className="gads-rel-token-label">Token</span>
            {tokenInfo.loading ? (
              <span className="gads-rel-pill gads-rel-pill-warn">validando...</span>
            ) : tokenInfo.error ? (
              <span className="gads-rel-pill gads-rel-pill-err">erro</span>
            ) : tokenInfo.connected ? (
              tokenInfo.access_token_valid ? (
                <span className="gads-rel-pill gads-rel-pill-ok">ok</span>
              ) : (
                <span className="gads-rel-pill gads-rel-pill-warn">renovando...</span>
              )
            ) : (
              <span className="gads-rel-pill gads-rel-pill-warn">sem integração</span>
            )}
          </div>

          {tokenInfo.access_token_expires_at ? (
            <div className="gads-rel-token-exp">
              expira em: {fmtBRDate(tokenInfo.access_token_expires_at)}
            </div>
          ) : null}

          {tokenInfo.error ? (
            <div className="gads-rel-token-err">{tokenInfo.error}</div>
          ) : null}
        </div>
      </div>

            {/* FILTROS (igual Meta: usuário + período) */}
      <div className="gads-rel-filters">
        <div className="gads-rel-filter gads-rel-filter-user">
          <label>Selecione o Usuário:</label>
          <select
            value={usuarioId}
              onChange={(e) => { setUsuarioId(e.target.value); setApplied(false); resetarRelatorioVisual(); }}
            disabled={!usuarios.length}
          >
            {isAdmin ? (
              <>
                <option value="">-- selecione --</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} {u.nome_empresa ? `(${u.nome_empresa})` : ""}
                  </option>
                ))}
              </>
            ) : (
              usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome || u.email || `Usuário ${u.id}`}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="gads-rel-filter gads-rel-filter-date">
<DateRangePresets
  label="Período"
  startDate={dateFromYmd(dateStart)}
  endDate={dateFromYmd(dateEnd)}
  onChange={({ start, end }) => {
    setDateStart(ymdLocal(start));
    setDateEnd(ymdLocal(end));
    setApplied(false);
    resetarRelatorioVisual();
  }}
  onApply={({ start, end }) => {
    aplicarPeriodo({
      date_start: ymdLocal(start),
      date_end: ymdLocal(end),
    });
  }}
/>



</div>


        <div className="gads-rel-filter gads-rel-actions">
          <button
            type="button"
            className="gads-rel-btn gads-rel-btn-primary"
            onClick={aplicarPeriodo}
            disabled={loadingAll}
            >
            Carregar
            </button>
        </div>
      </div>

      {errorAll && (
  <div className="gads-rel-alert gads-rel-alert-err">{errorAll}</div>
)}

{loadingAll && (
  <RelatorioSkeleton />
)}

{hasLoaded && !loadingAll && (

    
  <>




      {/* ALERTAS */}
      {errorResumo ? (
        <div className="gads-rel-alert gads-rel-alert-err">{errorResumo}</div>
      ) : null}


      {/* VISÃO GERAL (cards) */}
      <div className="gads-rel-section">
        <div className="gads-rel-section-title">Visão geral</div>

        {!totais && loadingResumo ? (
          <div className="gads-rel-loading">Carregando visão geral...</div>
        ) : null}

        {totais ? (
          <>

          <div className="gads-rel-footnote">
              Período: <b>{fmtBRDate(resumo?.periodo?.date_start)}</b> até{" "}
              <b>{fmtBRDate(resumo?.periodo?.date_end)}</b>
              {resumo?.customer?.customer_name ? (
                <>
                  {" "}
                  • Conta: <b>{resumo.customer.customer_name}</b>
                </>
              ) : null}
            </div>
            
            <div className="gads-rel-kpis">
              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Impressões</div>
                <div className="gads-rel-kpi-value">{fmtInt(totais.impressions)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("impressions")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Cliques</div>
                <div className="gads-rel-kpi-value">{fmtInt(totais.clicks)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("clicks")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">CTR</div>
                <div className="gads-rel-kpi-value">{fmtPct(totais.ctr)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("ctr")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Custo</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.cost)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("cost")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">CPC médio</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.average_cpc)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("average_cpc")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Conversões</div>
                <div className="gads-rel-kpi-value">{fmtNum(totais.conversions)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("conversions")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Taxa de conversão</div>
                <div className="gads-rel-kpi-value">{fmtPctBRFromRatio(convRate)}</div>
                <div className="gads-rel-kpi-desc">Conversões ÷ cliques</div>
                </div>


              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Custo/Conversão</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.cost_per_conversion)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("cost_per_conversion")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Valor conv.</div>
                <div className="gads-rel-kpi-value">{fmtNum(totais.conversions_value)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("conversions_value")}</div>
              </div>
            </div>

            
          </>
        ) : null}
      </div>

              {/* SESSÃO CAMPANHAS (sem busca/ordenar/botões) */}
<div className="gads-camp-section">
  <div className="gads-camp-head">
    <div className="gads-camp-title">Campanhas</div>

    {/* opcional: mostra período/conta em uma linha discreta */}
    <div className="gads-rel-footnote">
      Período: <b>{fmtBRDate(resumo?.periodo?.date_start)}</b> até{" "}
      <b>{fmtBRDate(resumo?.periodo?.date_end)}</b>
      {resumo?.customer?.customer_name ? (
        <>
          {" "}
          • Conta: <b>{resumo.customer.customer_name}</b>
        </>
      ) : null}
    </div>
  </div>

  {/* erro específico da seção campanhas (se você tiver) */}
  {errorCampanhas ? (
    <div className="gads-camp-alert gads-camp-alert-err">{errorCampanhas}</div>
  ) : null}

  {/* loading */}
  {loadingCampanhas ? (
    <div className="gads-camp-loading">Carregando campanhas...</div>
  ) : null}

  {/* tabela */}
  {!loadingCampanhas && Array.isArray(campanhas) ? (
    campanhas.length ? (
      <div className="gads-camp-tablewrap">
        <table className="gads-camp-table">
          <thead>
            <tr>
              <th>Campanha</th>
              <th className="gads-camp-center">Status</th>
              <th className="gads-camp-right">Impressões</th>
              <th className="gads-camp-right">Cliques</th>
              <th className="gads-camp-right">CTR</th>
              <th className="gads-camp-right">Custo</th>
              <th className="gads-camp-right">CPC médio</th>
              <th className="gads-camp-right">Conversões</th>
              <th className="gads-camp-right">Taxa de conv.</th>

              <th className="gads-camp-right">Custo/Conv</th>
              <th className="gads-camp-right">Valor conv.</th>
            </tr>
          </thead>

          <tbody>
  {campanhas.map((c) => (
    <tr key={c.campaign_id}>
      <td className="gads-camp-name" title={c.campaign_name || ""}>
        {c.campaign_name || "—"}
      </td>

      <td className="gads-camp-center">
        {renderStatusIcon(c.status)}
      </td>

      <td className="gads-camp-right">{fmtInt(c.impressions)}</td>
      <td className="gads-camp-right">{fmtInt(c.clicks)}</td>
      <td className="gads-camp-right">{fmtPctBR(c.ctr)}</td>
      <td className="gads-camp-right">{fmtMoneyBRL(c.cost)}</td>
      <td className="gads-camp-right">{fmtMoneyBRL(c.average_cpc)}</td>
      <td className="gads-camp-right">{fmtNum(c.conversions)}</td>
      <td className="gads-camp-right">{fmtTaxaConversao(c.conversions, c.clicks)}</td>
      <td className="gads-camp-right">{fmtMoneyBRL(c.cost_per_conversion)}</td>
      <td className="gads-camp-right">{fmtNum(c.conversions_value)}</td>
    </tr>
  ))}
</tbody>

        </table>
      </div>
    ) : (
      <div className="gads-camp-empty">Nenhuma campanha encontrada para o período.</div>
    )
  ) : null}
</div>

<section style={{ marginTop: 18 }}>
  <div
    className="gads-rel-section-evolucao"
    style={{ fontWeight: 800, marginBottom: 10 }}
  >
    Evolução diária da Conta
  </div>

  {!!dailyErr && <div className="gads-locs-error">{dailyErr}</div>}

  <div className="gads-rel-chartwrap">
    <Line
      data={{
        // ✅ Datas em PT-BR
        labels: dailyRows.map((x) => fmtBRDate(x.date)),
        datasets: [
          {
            label: "Conversões",
            data: dailyRows.map((x) => Number(x.conversions || 0)),
            yAxisID: "y",
          },
          {
            label: "Cliques",
            data: dailyRows.map((x) => Number(x.clicks || 0)),
            yAxisID: "y",
          },
          {
            // ✅ Custo em eixo separado (direita) pra não achatar as outras linhas
            label: "Custo (R$)",
            data: dailyRows.map((x) => Number(x.cost_micros || 0) / 1_000_000),
            yAxisID: "y1",
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              // Mantém o título com a data em PT-BR
              title: (items) => (items?.[0]?.label ? items[0].label : ""),
              // Formata o custo como moeda no tooltip
              label: (ctx) => {
                const label = ctx.dataset?.label || "";
                const v = Number(ctx.parsed?.y ?? 0);
                if (label.includes("Custo")) {
                  return `${label}: ${v.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}`;
                }
                return `${label}: ${v}`;
              },
            },
          },
        },
        scales: {
          // ✅ Eixo principal: conversões + cliques
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
          },
          // ✅ Eixo secundário: custo (R$)
          y1: {
            beginAtZero: true,
            position: "right",
            grid: { drawOnChartArea: false }, // não polui o fundo
            ticks: {
              callback: (v) =>
                Number(v).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  maximumFractionDigits: 0,
                }),
            },
          },
        },
      }}
      height={260}
    />
  </div>
</section>




{/* =========================
    TERMOS DE PESQUISA
========================= */}
<div className="gads-terms-section">
  <div className="gads-terms-head">
    <div className="gads-terms-title">Termos de pesquisa</div>
  </div>

  {errorTermos ? (
    <div className="gads-terms-alert gads-terms-alert-err">{errorTermos}</div>
  ) : null}

  {!loadingTermos && !errorTermos && (!termos || termos.length === 0) ? (
    <div className="gads-terms-empty">Nenhum termo encontrado para o período.</div>
  ) : null}

  {termos && termos.length > 0 ? (() => {
    // 1) Ordenação (front)
    const termosSorted = [...termos].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;

      const getField = (row, key) => {
        switch (key) {
          case "search_term":
            return row?.search_term || row?.searchTerm || row?.term || row?.query || "";
          case "match_type":
            return row?.match_type || row?.matchType || row?.keyword_match_type || row?.keywordMatchType || "";
          case "campaign_name":
            return row?.campaign_name || row?.campaignName || row?.campaign || "";
          case "conversion_rate":
            return Number(row?.conversion_rate ?? row?.conv_rate ?? 0);
          default:
            return row?.[key] ?? 0;
        }
      };

      const numKeys = new Set([
        "impressions",
        "clicks",
        "ctr",
        "cost",
        "average_cpc",
        "conversions",
        "conversion_rate",
        "cost_per_conversion",
        "conversions_value",
      ]);

      const va = getField(a, sortKey);
      const vb = getField(b, sortKey);

      if (numKeys.has(sortKey)) {
        const na = Number(va || 0);
        const nb = Number(vb || 0);
        if (na === nb) return 0;
        return na > nb ? dir : -dir;
      }

      const sa = String(va || "").toLowerCase();
      const sb = String(vb || "").toLowerCase();
      if (sa === sb) return 0;
      return sa > sb ? dir : -dir;
    });

    // 2) Paginação (front)
    const total = termosSorted.length;
    const size = Math.max(1, Number(pageSize || 25));
    const totalPages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(Math.max(1, Number(page || 1)), totalPages);

    const startIdx = (safePage - 1) * size;
    const endIdx = startIdx + size;
    const termosPage = termosSorted.slice(startIdx, endIdx);

    // helpers paginação
    const canPrev = safePage > 1;
    const canNext = safePage < totalPages;

    const from = total === 0 ? 0 : startIdx + 1;
    const to = Math.min(endIdx, total);

    // (opcional) lista curta de páginas tipo: 1 ... 4 5 6 ... 12
    const pages = (() => {
      const out = [];
      const push = (p) => out.push(p);

      if (totalPages <= 7) {
        for (let p = 1; p <= totalPages; p++) push(p);
        return out;
      }

      push(1);
      if (safePage > 3) push("...");
      const pStart = Math.max(2, safePage - 1);
      const pEnd = Math.min(totalPages - 1, safePage + 1);
      for (let p = pStart; p <= pEnd; p++) push(p);
      if (safePage < totalPages - 2) push("...");
      push(totalPages);
      return out;
    })();

    return (
      <>
        {/* BARRA DE PAGINAÇÃO (topo) */}
        <div className="gads-terms-pager">
          <div className="gads-terms-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-terms-pager-right">
            <label className="gads-terms-pagesize">
              Linhas:
              <select
                value={size}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setPage(safePage - 1)}
              disabled={!canPrev}
            >
              ◀
            </button>

            <div className="gads-terms-pagenums">
              {pages.map((p, i) => (
                p === "..." ? (
                  <span key={`dots-${i}`} className="gads-terms-dots">…</span>
                ) : (
                  <button
                    key={`p-${p}`}
                    type="button"
                    className={`gads-terms-pagenum ${p === safePage ? "is-active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              ))}
            </div>

            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setPage(safePage + 1)}
              disabled={!canNext}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="gads-terms-tablewrap">
          <table className="gads-terms-table">
            <thead>
              <tr>
                <th className="gads-terms-left gads-terms-col-term">
                  {headerSortLabel("search_term", "Termo")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("match_type", "Tipo")}
                </th>

                <th className="gads-terms-center gads-terms-col-camp">
                  {headerSortLabel("campaign_name", "Campanha")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("impressions", "Impressões")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("clicks", "Cliques")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("ctr", "CTR")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("cost", "Custo")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("average_cpc", "CPC médio")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("conversions", "Conversões")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("conversion_rate", "Taxa conv.")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("cost_per_conversion", "Custo/Conv")}
                </th>

                <th className="gads-terms-center">
                  {headerSortLabel("conversions_value", "Valor conv.")}
                </th>
              </tr>
            </thead>

            <tbody>
              {termosPage.map((t, idx) => {
                const term =
                  t.search_term || t.searchTerm || t.term || t.query || "—";
                const match =
                  t.match_type || t.matchType || t.keyword_match_type || t.keywordMatchType || "—";
                const camp =
                  t.campaign_name || t.campaignName || t.campaign || "—";

                const impressions = Number(t.impressions || 0);
                const clicks = Number(t.clicks || 0);
                const ctr = t.ctr ?? (impressions > 0 ? clicks / impressions : 0);

                const cost = Number(t.cost ?? t.cost_value ?? 0);
                const cpc = t.average_cpc ?? (clicks > 0 ? cost / clicks : 0);

                const conv = Number(t.conversions || 0);
                const convRate = clicks > 0 ? (conv / clicks) : 0;

                const costPerConv = t.cost_per_conversion ?? (conv > 0 ? cost / conv : 0);
                const convValue = Number(t.conversions_value ?? t.conversionsValue ?? 0);

                return (
                  <tr key={t.id || `${term}-${startIdx + idx}`}>
                    <td className="gads-terms-left gads-terms-col-term" title={term}>{term}</td>
                    <td className="gads-terms-center">{String(match).toUpperCase()}</td>
                    <td className="gads-terms-center gads-terms-col-camp" title={camp}>{camp}</td>

                    <td className="gads-terms-center">{fmtInt(impressions)}</td>
                    <td className="gads-terms-center">{fmtInt(clicks)}</td>
                    <td className="gads-terms-center">{fmtPctBR ? fmtPctBR(ctr) : fmtPct(ctr)}</td>

                    <td className="gads-terms-center">{fmtMoneyBRL ? fmtMoneyBRL(cost) : fmtBRL(cost)}</td>
                    <td className="gads-terms-center">{fmtMoneyBRL ? fmtMoneyBRL(cpc) : fmtBRL(cpc)}</td>

                    <td className="gads-terms-center">{fmtNum(conv)}</td>
                    <td className="gads-terms-center">{fmtPctBR ? fmtPctBR(convRate) : fmtPct(convRate)}</td>

                    <td className="gads-terms-center">
                      {fmtMoneyBRL ? fmtMoneyBRL(costPerConv) : fmtBRL(costPerConv)}
                    </td>
                    <td className="gads-terms-center">{fmtNum(convValue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* BARRA DE PAGINAÇÃO (rodapé) */}
        <div className="gads-terms-pager gads-terms-pager-bottom">
          <div className="gads-terms-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-terms-pager-right">
            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setPage(safePage - 1)}
              disabled={!canPrev}
            >
              ◀
            </button>
            <span className="gads-terms-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>
            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setPage(safePage + 1)}
              disabled={!canNext}
            >
              ▶
            </button>
          </div>
        </div>
      </>
    );
  })() : null}
</div>

{/* =========================
    GRUPOS DE ANÚNCIOS
========================= */}
<div className="gads-groups-section">
  <div className="gads-groups-head">
    <div className="gads-groups-title">Grupos de anúncios</div>
  </div>

  {errorGrupos ? (
    <div className="gads-groups-alert gads-groups-alert-err">{errorGrupos}</div>
  ) : null}

  {!loadingGrupos && !errorGrupos && (!grupos || grupos.length === 0) ? (
    <div className="gads-groups-empty">Nenhum grupo encontrado para o período.</div>
  ) : null}

  {grupos && grupos.length > 0 ? (() => {
    // 1) Ordenação (front)
    const gruposSorted = [...grupos].sort((a, b) => {
      const dir = gruposSortDir === "asc" ? 1 : -1;

      const getNum = (x, k) => Number(x?.metrics?.[k] ?? 0);
      const getTxt = (x, k) => String(x?.[k] ?? "").toLowerCase();

      const aCamp = String(a?.campaign?.name ?? "");
      const bCamp = String(b?.campaign?.name ?? "");
      const aGrp  = String(a?.ad_group?.name ?? "");
      const bGrp  = String(b?.ad_group?.name ?? "");

      // textos especiais
      if (gruposSortKey === "campaign_name") {
        return aCamp.localeCompare(bCamp, "pt-BR") * dir;
      }
      if (gruposSortKey === "ad_group_name") {
        return aGrp.localeCompare(bGrp, "pt-BR") * dir;
      }

      // numéricos (metrics.*)
      const va = getNum(a, gruposSortKey);
      const vb = getNum(b, gruposSortKey);
      if (vb === va) return 0;
      return (va > vb ? 1 : -1) * dir;
    });

    // 2) Paginação (front)
    const total = gruposSorted.length;
    const size = Math.max(1, Number(gruposPageSize || 50));
    const totalPages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(Math.max(1, Number(gruposPage || 1)), totalPages);

    const startIdx = (safePage - 1) * size;
    const endIdx = startIdx + size;
    const gruposPageRows = gruposSorted.slice(startIdx, endIdx);

    const canPrev = safePage > 1;
    const canNext = safePage < totalPages;

    const from = total === 0 ? 0 : startIdx + 1;
    const to = Math.min(endIdx, total);

    const pages = (() => {
      const out = [];
      const push = (p) => out.push(p);

      if (totalPages <= 7) {
        for (let p = 1; p <= totalPages; p++) push(p);
        return out;
      }

      push(1);
      if (safePage > 3) push("...");
      const pStart = Math.max(2, safePage - 1);
      const pEnd = Math.min(totalPages - 1, safePage + 1);
      for (let p = pStart; p <= pEnd; p++) push(p);
      if (safePage < totalPages - 2) push("...");
      push(totalPages);
      return out;
    })();

    return (
      <>
        {/* PAGER (topo) */}
        <div className="gads-groups-pager">
          <div className="gads-groups-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-groups-pager-right">
            <label className="gads-groups-pagesize">
              Linhas:
              <select
                value={size}
                onChange={(e) => { setGruposPageSize(Number(e.target.value)); setGruposPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button
              type="button"
              className="gads-groups-pagebtn"
              onClick={() => setGruposPage(safePage - 1)}
              disabled={!canPrev}
            >
              ◀
            </button>

            <div className="gads-groups-pagenums">
              {pages.map((p, i) => (
                p === "..." ? (
                  <span key={`gdots-${i}`} className="gads-groups-dots">…</span>
                ) : (
                  <button
                    key={`gp-${p}`}
                    type="button"
                    className={`gads-groups-pagenum ${p === safePage ? "is-active" : ""}`}
                    onClick={() => setGruposPage(p)}
                  >
                    {p}
                  </button>
                )
              ))}
            </div>

            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setGruposPage(safePage + 1)}
              disabled={!canNext}
            >
              ▶
            </button>
          </div>
        </div>

        <div className="gads-groups-tablewrap">
          <table className="gads-groups-table">
            <thead>
              <tr>
                <th
                  className="gads-groups-left gads-groups-th gads-groups-th-click gads-groups-col-camp"
                  onClick={() => { toggleSortGrupos("campaign_name"); setGruposPage(1); }}
                  role="button"
                  title="Ordenar por Campanha"
                >
                  Campanha {gruposSortKey === "campaign_name" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-left gads-groups-th gads-groups-th-click gads-groups-col-group"
                  onClick={() => { toggleSortGrupos("ad_group_name"); setGruposPage(1); }}
                  role="button"
                  title="Ordenar por Grupo"
                >
                  Grupo de anúncios {gruposSortKey === "ad_group_name" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("impressions"); setGruposPage(1); }}
                  role="button"
                >
                  Impressões {gruposSortKey === "impressions" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("clicks"); setGruposPage(1); }}
                  role="button"
                >
                  Cliques {gruposSortKey === "clicks" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("ctr"); setGruposPage(1); }}
                  role="button"
                >
                  CTR {gruposSortKey === "ctr" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("cost"); setGruposPage(1); }}
                  role="button"
                >
                  Custo {gruposSortKey === "cost" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("average_cpc"); setGruposPage(1); }}
                  role="button"
                >
                  CPC médio {gruposSortKey === "average_cpc" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("conversions"); setGruposPage(1); }}
                  role="button"
                >
                  Conversões {gruposSortKey === "conversions" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("cost_per_conversion"); setGruposPage(1); }}
                  role="button"
                >
                  Custo/Conv {gruposSortKey === "cost_per_conversion" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th
                  className="gads-groups-center gads-groups-th gads-groups-th-click"
                  onClick={() => { toggleSortGrupos("conversions_value"); setGruposPage(1); }}
                  role="button"
                >
                  Valor conv. {gruposSortKey === "conversions_value" ? (gruposSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {gruposPageRows.map((r, idx) => {
                const camp = r?.campaign?.name || "—";
                const grp = r?.ad_group?.name || "—";
                const m = r?.metrics || {};

                const impr = Number(m.impressions || 0);
                const clicks = Number(m.clicks || 0);
                const ctr = Number(m.ctr ?? (impr > 0 ? clicks / impr : 0));

                const cost = Number(m.cost ?? 0);
                const cpc = Number(m.average_cpc ?? (clicks > 0 ? cost / clicks : 0));

                const conv = Number(m.conversions || 0);
                const cpa = Number(m.cost_per_conversion ?? (conv > 0 ? cost / conv : 0));

                const convValue = Number(m.conversions_value ?? 0);

                return (
                  <tr key={`${r?.ad_group?.id || "ag"}-${startIdx + idx}`}>
                    <td className="gads-groups-left gads-groups-col-camp" title={camp}>{camp}</td>
                    <td className="gads-groups-left gads-groups-col-group" title={grp}>{grp}</td>

                    <td className="gads-groups-center">{fmtInt(impr)}</td>
                    <td className="gads-groups-center">{fmtInt(clicks)}</td>
                    <td className="gads-groups-center">{fmtPctBR ? fmtPctBR(ctr) : fmtPct(ctr)}</td>

                    <td className="gads-groups-center">{fmtMoneyBRL ? fmtMoneyBRL(cost) : fmtBRL(cost)}</td>
                    <td className="gads-groups-center">{fmtMoneyBRL ? fmtMoneyBRL(cpc) : fmtBRL(cpc)}</td>

                    <td className="gads-groups-center">{fmtNum(conv)}</td>

                    <td className="gads-groups-center">
                      {fmtMoneyBRL ? fmtMoneyBRL(cpa) : fmtBRL(cpa)}
                    </td>

                    <td className="gads-groups-center">{fmtNum(convValue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGER (rodapé) */}
        <div className="gads-groups-pager gads-groups-pager-bottom">
          <div className="gads-groups-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-groups-pager-right">
            <button
              type="button"
              className="gads-groups-pagebtn"
              onClick={() => setGruposPage(safePage - 1)}
              disabled={!canPrev}
            >
              ◀
            </button>

            <span className="gads-terms-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>

            <button
              type="button"
              className="gads-terms-pagebtn"
              onClick={() => setGruposPage(safePage + 1)}
              disabled={!canNext}
            >
              ▶
            </button>
          </div>
        </div>
      </>
    );
  })() : null}
</div>

{/* =========================
    GRÁFICO: Comparativo (Top 2 campanhas) - Métricas indexadas (CTR em eixo separado)
========================= */}
{top2CampanhasPorConv?.length === 2 ? (
  <section style={{ marginTop: 18, marginBottom: 34 }}>
    <div className="gads-rel-section-evolucao" style={{ fontWeight: 800, marginBottom: 10 }}>
      Comparativo (Top 2 campanhas): Métricas principais
    </div>

    <div className="gads-rel-chartwrap" style={{ height: 380 }}>
      <Bar
        data={{
          labels: ["Impressões", "Custo", "Conversões", "Cliques"],
          datasets: [
            // ======= BARRAS (Campanha 1) =======
            {
              type: "bar",
              label: top2CampanhasPorConv[0]?.campaign_name || top2CampanhasPorConv[0]?.nome || "Campanha 1",
              data: [
                Number(top2CampanhasPorConv[0]?.impressions || 0),
                Number(top2CampanhasPorConv[0]?.cost || 0),
                Number(top2CampanhasPorConv[0]?.conversions || 0),
                Number(top2CampanhasPorConv[0]?.clicks || 0),
              ],
              yAxisID: "y",
            },

            // ======= BARRAS (Campanha 2) =======
            {
              type: "bar",
              label: top2CampanhasPorConv[1]?.campaign_name || top2CampanhasPorConv[1]?.nome || "Campanha 2",
              data: [
                Number(top2CampanhasPorConv[1]?.impressions || 0),
                Number(top2CampanhasPorConv[1]?.cost || 0),
                Number(top2CampanhasPorConv[1]?.conversions || 0),
                Number(top2CampanhasPorConv[1]?.clicks || 0),
              ],
              yAxisID: "y",
            },

            // ======= LINHA CTR (Campanha 1) =======
            {
              type: "line",
              label: `CTR (${top2CampanhasPorConv[0]?.campaign_name || top2CampanhasPorConv[0]?.nome || "Campanha 1"})`,
              data: [
                null, // Impressões
                null, // Custo
                null, // Conversões
                null, // Cliques
              ],
              // ⚠️ truque: usar "tooltip" separado com ponto único via parsing? (não)
              // ✅ solução simples e robusta: duplicar labels com CTR em dataset separado abaixo (ver options -> plugins.tooltip)
              yAxisID: "y1",
              tension: 0.25,
              pointRadius: 0,
              borderWidth: 2,
              hidden: true, // só aparece no tooltip/legenda se você quiser (tirar se quiser ver a linha)
            },

            // ======= LINHA CTR (Campanha 2) =======
            {
              type: "line",
              label: `CTR (${top2CampanhasPorConv[1]?.campaign_name || top2CampanhasPorConv[1]?.nome || "Campanha 2"})`,
              data: [null, null, null, null],
              yAxisID: "y1",
              tension: 0.25,
              pointRadius: 0,
              borderWidth: 2,
              hidden: true,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                // ✅ adiciona CTR no tooltip mesmo sem estar no eixo/barra
                afterBody: () => {
                  const c1 = Number(top2CampanhasPorConv[0]?.ctr || 0) * 100;
                  const c2 = Number(top2CampanhasPorConv[1]?.ctr || 0) * 100;

                  const n1 = top2CampanhasPorConv[0]?.campaign_name || top2CampanhasPorConv[0]?.nome || "Campanha 1";
                  const n2 = top2CampanhasPorConv[1]?.campaign_name || top2CampanhasPorConv[1]?.nome || "Campanha 2";

                  return [
                    "",
                    `CTR (${n1}): ${c1.toFixed(2).replace(".", ",")}%`,
                    `CTR (${n2}): ${c2.toFixed(2).replace(".", ",")}%`,
                  ];
                },
                label: (ctx) => {
                  const label = ctx.dataset.label || "";
                  const v = Number(ctx.raw || 0);
                  const metric = String(ctx.label || "");

                  if (metric === "Custo") return `${label}: R$ ${v.toFixed(2).replace(".", ",")}`;
                  return `${label}: ${Number.isInteger(v) ? v : v.toFixed(2)}`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              position: "left",
            },
            // eixo do CTR (direita) — mesmo se a linha estiver "hidden", mantém pronto
            y1: {
              beginAtZero: true,
              position: "right",
              grid: { drawOnChartArea: false },
              ticks: { callback: (v) => `${v}%` },
            },
          },
        }}
      />
    </div>
  </section>
) : null}





{/* =========================
    ANÚNCIOS
========================= */}
<div className="gads-ads-section">
  <div className="gads-ads-head">
    <div className="gads-ads-title">Anúncios</div>
    {campanhaSelecionadaId ? (
      <div className="gads-ads-sub">Filtrando pela campanha selecionada.</div>
    ) : null}
  </div>

  {errorAnuncios ? (
    <div className="gads-ads-alert gads-ads-alert-err">{errorAnuncios}</div>
  ) : null}

  {loadingAnuncios ? (
    <div className="gads-ads-loading">Carregando anúncios...</div>
  ) : null}

  {!loadingAnuncios && !errorAnuncios && (!anuncios || anuncios.length === 0) ? (
    <div className="gads-ads-empty">Nenhum anúncio encontrado para o período.</div>
  ) : null}

  {anuncios && anuncios.length > 0 ? (() => {
    // ordenação
    const sorted = [...anuncios].sort((a, b) => {
      const dir = anunciosSortDir === "asc" ? 1 : -1;

      const num = (x, k) => Number(x?.[k] ?? 0);
      const text = (x, k) => String(x?.[k] ?? "").toLowerCase();

      if (anunciosSortKey === "campaign_name") return text(a, "campaign_name").localeCompare(text(b, "campaign_name"), "pt-BR") * dir;
      if (anunciosSortKey === "ad_group_name") return text(a, "ad_group_name").localeCompare(text(b, "ad_group_name"), "pt-BR") * dir;

      const va = num(a, anunciosSortKey);
      const vb = num(b, anunciosSortKey);
      if (vb === va) return 0;
      return (va > vb ? 1 : -1) * dir;
    });

    // paginação
    const total = sorted.length;
    const size = Math.max(1, Number(anunciosPageSize || 25));
    const totalPages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(Math.max(1, Number(anunciosPage || 1)), totalPages);

    const startIdx = (safePage - 1) * size;
    const endIdx = startIdx + size;
    const pageRows = sorted.slice(startIdx, endIdx);

    const canPrev = safePage > 1;
    const canNext = safePage < totalPages;
    const from = total === 0 ? 0 : startIdx + 1;
    const to = Math.min(endIdx, total);

    return (
      <>
        {/* pager topo */}
        <div className="gads-ads-pager">
          <div className="gads-ads-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-ads-pager-right">
            <label className="gads-ads-pagesize">
              Linhas:
              <select
                value={size}
                onChange={(e) => { setAnunciosPageSize(Number(e.target.value)); setAnunciosPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button type="button" className="gads-ads-pagebtn" onClick={() => setAnunciosPage(safePage - 1)} disabled={!canPrev}>◀</button>

            <span className="gads-ads-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>

            <button type="button" className="gads-ads-pagebtn" onClick={() => setAnunciosPage(safePage + 1)} disabled={!canNext}>▶</button>
          </div>
        </div>

        <div className="gads-ads-tablewrap">
          <table className="gads-ads-table">
            <thead>
              <tr>
                <th className="gads-ads-left gads-ads-th" onClick={() => toggleSortAnuncios("campaign_name")} role="button">
                  Campanha {anunciosSortKey === "campaign_name" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-left gads-ads-th" onClick={() => toggleSortAnuncios("ad_group_name")} role="button">
                  Grupo {anunciosSortKey === "ad_group_name" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>

                <th className="gads-ads-left">Preview</th>

                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("impressions")} role="button">
                  Impr. {anunciosSortKey === "impressions" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("clicks")} role="button">
                  Cliques {anunciosSortKey === "clicks" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("ctr")} role="button">
                  CTR {anunciosSortKey === "ctr" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("cost")} role="button">
                  Custo {anunciosSortKey === "cost" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("conversions")} role="button">
                  Conv. {anunciosSortKey === "conversions" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="gads-ads-center gads-ads-th" onClick={() => toggleSortAnuncios("cost_per_conversion")} role="button">
                  Custo/Conv {anunciosSortKey === "cost_per_conversion" ? (anunciosSortDir === "asc" ? "▲" : "▼") : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {pageRows.map((row, i) => {
                const prev = buildAdPreview(row);
                const ctr = row?.ctr ?? (Number(row?.impressions || 0) > 0 ? Number(row?.clicks || 0) / Number(row?.impressions || 0) : 0);

                return (
                  <tr key={`${row?.ad_id || "ad"}-${startIdx + i}`}>
                    <td className="gads-ads-left" title={row?.campaign_name || ""}>{row?.campaign_name || "—"}</td>
                    <td className="gads-ads-left" title={row?.ad_group_name || ""}>{row?.ad_group_name || "—"}</td>

                    <td className="gads-ads-left">
                      <div className="gads-adprev">
                        <div className="gads-adprev-type">{getAdTypeLabel(prev.type || "AD")}</div>
                        <div className="gads-adprev-head">{prev.headline || "—"}</div>
                        <div className="gads-adprev-url" title={prev.finalUrl || ""}>{prev.displayUrl || prev.finalUrl || "—"}</div>
                        {prev.desc ? <div className="gads-adprev-desc">{prev.desc}</div> : null}
                      </div>
                    </td>

                    <td className="gads-ads-center">{fmtInt(row?.impressions || 0)}</td>
                    <td className="gads-ads-center">{fmtInt(row?.clicks || 0)}</td>
                    <td className="gads-ads-center">{fmtPctBR ? fmtPctBR(ctr) : fmtPct(ctr)}</td>
                    <td className="gads-ads-center">{fmtMoneyBRL ? fmtMoneyBRL(row?.cost || 0) : fmtBRL(row?.cost || 0)}</td>
                    <td className="gads-ads-center">{fmtNum(row?.conversions || 0)}</td>
                    <td className="gads-ads-center">{fmtMoneyBRL ? fmtMoneyBRL(row?.cost_per_conversion || 0) : fmtBRL(row?.cost_per_conversion || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* pager rodapé */}
        <div className="gads-ads-pager gads-ads-pager-bottom">
          <div className="gads-ads-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-ads-pager-right">
            <button type="button" className="gads-ads-pagebtn" onClick={() => setAnunciosPage(safePage - 1)} disabled={!canPrev}>◀</button>
            <span className="gads-ads-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>
            <button type="button" className="gads-ads-pagebtn" onClick={() => setAnunciosPage(safePage + 1)} disabled={!canNext}>▶</button>
          </div>
        </div>
      </>
    );
  })() : null}
</div>

{/* =========================
    LOCALIZAÇÕES
========================= */}
<div className="gads-locs-section">
  <div className="gads-locs-head">
    <div className="gads-locs-title">Localizações</div>

  </div>

  {locsError ? (
    <div className="gads-locs-error">{locsError}</div>
  ) : null}

  {loadingLocs ? (
    <div className="gads-locs-loading">Carregando localizações…</div>
  ) : null}

  {!loadingLocs && !locsError ? (() => {
    const rows = Array.isArray(locs) ? locs : [];

    // ordenação
    const sorted = [...rows].sort((a, b) => {
      const dir = locsSortDir === "asc" ? 1 : -1;
      const va = Number(a?.[locsSortKey] ?? 0);
      const vb = Number(b?.[locsSortKey] ?? 0);
      if (va === vb) return 0;
      return va > vb ? dir : -dir;
    });

    // paginação
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / locsPageSize));
    const safePage = Math.min(Math.max(1, locsPage), totalPages);
    const start = (safePage - 1) * locsPageSize;
    const end = start + locsPageSize;
    const pageRows = sorted.slice(start, end);

    const from = total === 0 ? 0 : start + 1;
    const to = Math.min(end, total);

    function toggleSort(key) {
      if (locsSortKey === key) {
        setLocsSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setLocsSortKey(key);
        setLocsSortDir("desc");
      }
      setLocsPage(1);
    }

    function headerSortLabel(key, label) {
      const active = locsSortKey === key;
      const arrow = !active ? "" : (locsSortDir === "asc" ? " ▲" : " ▼");
      return (
        <button
          type="button"
          className={`gads-locs-thbtn ${active ? "is-active" : ""}`}
          onClick={() => toggleSort(key)}
          title="Ordenar"
        >
          {label}{arrow}
        </button>
      );
    }

    return (
      <>
        {/* barra topo */}
        <div className="gads-locs-pager">
          <div className="gads-locs-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-locs-pager-right">
            <label className="gads-locs-pagesize">
              Linhas:
              <select
                value={locsPageSize}
                onChange={(e) => { setLocsPageSize(Number(e.target.value)); setLocsPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button
              type="button"
              className="gads-locs-pagebtn"
              onClick={() => setLocsPage(safePage - 1)}
              disabled={safePage <= 1}
            >
              ◀
            </button>

            <span className="gads-locs-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>

            <button
              type="button"
              className="gads-locs-pagebtn"
              onClick={() => setLocsPage(safePage + 1)}
              disabled={safePage >= totalPages}
            >
              ▶
            </button>
          </div>
        </div>

        {/* tabela */}
        <div className="gads-locs-tablewrap">
          <table className="gads-locs-table">
            <thead>
              <tr>
                <th className="gads-locs-th">Cidade</th>

                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("impressions", "Impr.")}</th>
                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("clicks", "Cliques")}</th>
                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("ctr", "CTR")}</th>

                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("cost", "Custo")}</th>
                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("average_cpc", "CPC")}</th>

                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("conversions", "Conv.")}</th>
                <th className="gads-locs-th gads-locs-thnum">{headerSortLabel("cost_per_conversion", "Custo/Conv")}</th>
              </tr>
            </thead>

            <tbody>
              {pageRows.map((r, idx) => {
                const country =
                r?.country_name ||
                r?.country_code ||
                r?.geo_target_country ||
                "-";

                const region =
                r?.region_name ||
                r?.geo_target_region ||
                "-";

                const city =
                r?.city_name ||
                r?.geo_target_city ||
                r?.name ||
                "-";


                const impressions = Number(r?.impressions ?? 0);
                const clicks      = Number(r?.clicks ?? 0);
                const ctr         = Number(r?.ctr ?? 0);

                const cost         = Number(r?.cost ?? 0);
                const cpc          = Number(r?.average_cpc ?? 0);
                const conv         = Number(r?.conversions ?? 0);
                const costPerConv  = Number(r?.cost_per_conversion ?? 0);

                return (
                  <tr key={`${r?.criterion_id || r?.city_criterion_id || city}|${idx}`}>
                    <td className="gads-locs-td">{city}</td>

                    <td className="gads-locs-td gads-locs-tdnum">{fmtInt(impressions)}</td>
                    <td className="gads-locs-td gads-locs-tdnum">{fmtInt(clicks)}</td>
                    <td className="gads-locs-td gads-locs-tdnum">{fmtPctBR ? fmtPctBR(ctr) : fmtPct(ctr)}</td>

                    <td className="gads-locs-td gads-locs-tdnum">{fmtMoneyBRL ? fmtMoneyBRL(cost) : fmtBRL(cost)}</td>
                    <td className="gads-locs-td gads-locs-tdnum">{fmtMoneyBRL ? fmtMoneyBRL(cpc) : fmtBRL(cpc)}</td>

                    <td className="gads-locs-td gads-locs-tdnum">{fmtNum(conv)}</td>
                    <td className="gads-locs-td gads-locs-tdnum">{fmtMoneyBRL ? fmtMoneyBRL(costPerConv) : fmtBRL(costPerConv)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* barra rodapé */}
        <div className="gads-locs-pager gads-locs-pager-bottom">
          <div className="gads-locs-pager-left">
            Mostrando <b>{from}</b>–<b>{to}</b> de <b>{total}</b>
          </div>

          <div className="gads-locs-pager-right">
            <button
              type="button"
              className="gads-locs-pagebtn"
              onClick={() => setLocsPage(safePage - 1)}
              disabled={safePage <= 1}
            >
              ◀
            </button>
            <span className="gads-locs-pageinfo">
              Página <b>{safePage}</b> de <b>{totalPages}</b>
            </span>
            <button
              type="button"
              className="gads-locs-pagebtn"
              onClick={() => setLocsPage(safePage + 1)}
              disabled={safePage >= totalPages}
            >
              ▶
            </button>
          </div>
        </div>
      </>
    );
  })() : null}
</div>
     
{/* =========================
    ANÁLISE E RECOMENDAÇÕES (IA)
========================= */}
<section className="gads-ai-wrap">
  {/* Header empilhado (título + botão abaixo, centralizado) */}
  <div className="gads-ai-head gads-ai-head--stack">
    <div className="gads-ai-title">Análise e recomendações (IA)</div>

    <div className="gads-ai-actions gads-ai-actions--below">
      <button
        type="button"
        className={`gads-ai-btn ${aiLoading ? "is-loading" : ""}`}
        onClick={() => carregarAnaliseIA({ force: false })}
        disabled={aiLoading || !usuarioId}
        title={!usuarioId ? "Selecione um usuário" : ""}
      >
        {aiLoading ? "Gerando..." : "Receber análise e recomendações"}
      </button>
    </div>
  </div>

  {/* Frase de demora (somente no loading) + piscando via CSS */}
  {aiLoading && (
    <div className="gads-ai-hint is-loading">
      Esse processo pode demorar alguns segundos. Levante, estique-se e tome um café!
    </div>
  )}

  {!!aiError && <div className="gads-ai-error">{aiError}</div>}

  {/* Toggle (aparece quando já tem resultado, mesmo recolhido) */}
  {aiData?.analysis && (
    <div className="gads-ai-collapse">
      <button
        type="button"
        className="gads-ai-iconbtn"
        onClick={() => setAiExpanded((v) => !v)}
        aria-label={aiExpanded ? "Recolher análise" : "Expandir análise"}
        title={aiExpanded ? "Recolher análise" : "Expandir análise"}
      >
        {aiExpanded ? <ArrowUpFromLine size={22} /> : <ArrowDownFromLine size={22} />}
      </button>
    </div>
  )}

  {/* Corpo (só quando expandido) */}
  {aiExpanded && aiData?.analysis && (
    <div className="gads-ai-body">
      <div className="gads-ai-note">
        A análise da IA é baseada nos números exibidos acima. Ainda assim, é essencial considerar o
        contexto das campanhas e os objetivos reais do negócio. Por isso, tome as decisões em
        conjunto com o seu gestor de tráfego pago.
      </div>

      <div className="gads-ai-sub">
        <div>Gerado em: {fmtBRDate(aiData?.generated_at || "")}</div>
        <div>
          Período: {fmtBRDate(aiData?.scope?.filters?.date_start || dateStart)} →{" "}
          {fmtBRDate(aiData?.scope?.filters?.date_end || dateEnd)}
        </div>
      </div>

      <div className="gads-ai-block">
        <div className="gads-ai-h2">Resumo executivo</div>
        <ul className="gads-ai-ul">
          {(aiData.analysis.executive_summary || []).map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>

      <div className="gads-ai-block">
        <div className="gads-ai-h2">Diagnóstico</div>
        <ul className="gads-ai-ul">
          {(aiData.analysis.diagnostics || []).map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>

      <div className="gads-ai-block">
        <div className="gads-ai-h2">Recomendações</div>

        <div className="gads-ai-h3">Alto impacto</div>
        {renderRecCards(aiData.analysis?.recommendations?.high_impact)}

        <div className="gads-ai-h3">Vitórias rápidas (≈ 30 min)</div>
        {renderRecCards(aiData.analysis?.recommendations?.quick_wins_30min)}

        <div className="gads-ai-h3">Ações táticas (1–2 dias)</div>
        {renderRecCards(aiData.analysis?.recommendations?.tactical_1_2days)}

        <div className="gads-ai-h3">Estratégico (1–2 semanas)</div>
        {renderRecCards(aiData.analysis?.recommendations?.strategic_1_2weeks)}
      </div>

      <div className="gads-ai-two">
        <div className="gads-ai-block">
          <div className="gads-ai-h2">Redução de desperdício</div>
          <ul className="gads-ai-ul">
            {(aiData.analysis.waste_reduction || []).map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>

        <div className="gads-ai-block">
          <div className="gads-ai-h2">Oportunidades de crescimento</div>
          <ul className="gads-ai-ul">
            {(aiData.analysis.growth_opportunities || []).map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="gads-ai-two">
        <div className="gads-ai-block">
          <div className="gads-ai-h2">Riscos e pontos de atenção</div>
          <ul className="gads-ai-ul">
            {(aiData.analysis.risks_and_watchouts || []).map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>

        {Array.isArray(aiData.analysis.limitations) && aiData.analysis.limitations.length > 0 && (
          <div className="gads-ai-block">
            <div className="gads-ai-h2">Limitações</div>
            <ul className="gads-ai-ul">
              {aiData.analysis.limitations.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )}
</section>



          </>
)}

      </div>

      
    </div>
  );
}
